import { runtimeGraphIds, RuntimeNodeImport } from '@/models/logic/RuntimeGraphIds';
import { runInAction } from 'mobx';
import type Context from '@/context';
import type {
  IChipModel,
  ITileManagerModel,
  IStructureCircuitModel,
  ILogicCircuitModel,
  ICircuitModel,
} from '@/interfaces/models';
import { ChipModel, InterfaceLink, InterfaceType } from '@/models/ChipModel';
import { TileType } from '@/models/TileTypes';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { CircuitType } from '@/schemas/common';
import { createChipId } from '@/types/ids';
import type { InterfaceLinkInstanceId, ChipInstanceId } from '@/types/ids';
import { ChipInstanceModelFactory } from '@/factories/ChipInstanceModelFactory';
import { CircuitModelFactory } from '@/factories/CircuitModelFactory';
import { LogicCircuitModelFactory } from '@/factories/LogicCircuitModelFactory';
import { StructureCircuitModelFactory } from '@/factories/StructureCircuitModelFactory';
import type { ChipModelDto } from '@/schemas/chip';
import { ChipInstanceModel, type ChipInstanceDto } from '@/models/ChipInstanceModel';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { Point, type PointData } from '@/models/Point';
import type { InterfaceTileState } from '@/schemas/tiles';
import type { CircuitDto } from '@/models/CircuitModel';
import type { ChipId } from '@/types/ids';

export class ChipModelFactory {
  constructor(
    private readonly circuitModelFactory: CircuitModelFactory,
    private readonly logicCircuitModelFactory: LogicCircuitModelFactory,
    private readonly structureCircuitModelFactory: StructureCircuitModelFactory,
    private readonly chipInstanceModelFactory: ChipInstanceModelFactory,
  ) {}

  create(
    context: Context,
    options?: {
      kind?: 'bundle-adapter';
      templateType?: 'logic' | 'structure';
      templateOrigin?: PointData;
      name?: string;
      description?: string;
      color?: string;
    },
  ): IChipModel {
    const chipId = createChipId();

    const logicCircuitModel = this.logicCircuitModelFactory.create(
      this.circuitModelFactory.create(context, CircuitType.ChipLogic),
      context.app.userPreferencesModel.simulationConfig,
      context,
    );
    const structureCircuitModel = this.structureCircuitModelFactory.create(
      this.circuitModelFactory.create(context, CircuitType.ChipStructure),
    );

    const editingInstance = this.chipInstanceModelFactory.createEditingInstance(
      context,
      chipId,
    );

    return new ChipModel({
      context,
      id: chipId,
      kind: options?.kind,
      templateType: options?.templateType,
      templateOrigin: options?.templateOrigin,
      name: options?.name ?? `Chip ${chipId.slice(-4)}`, // Default name using last 4 chars of ID
      description: options?.description ?? '',
      color: options?.color ?? `hsl(${Math.random() * 360}, 70%, 50%)`, // Random color by default
      editingInstance,
      logicCircuit: logicCircuitModel,
      structureCircuit: structureCircuitModel,
    });
  }

  *fromDtoSteps(
    context: Context,
    dto: ChipModelDto,
    onProgress?: import('@/utils/projectLoadProgress').ProjectLoadWorkObserver,
  ): Generator<void, IChipModel> {
    const importer = runtimeGraphIds(context).importer();
    const logicTiles = dto.logicCircuitDto.tileManagerDto.tiles.length;
    const totalTiles = logicTiles + dto.structureCircuitDto.tileManagerDto.tiles.length;
    const logicManager = yield* this.circuitModelFactory.tileManagerFromDtoSteps(
      context,
      dto.logicCircuitDto as CircuitDto,
      importer,
      onProgress && ((completed) => onProgress(completed, totalTiles)),
    );
    const structureManager = yield* this.circuitModelFactory.tileManagerFromDtoSteps(
      context,
      dto.structureCircuitDto as CircuitDto,
      importer,
      onProgress && ((completed) => onProgress(logicTiles + completed, totalTiles)),
    );
    return this.fromDto(context, dto, { logicManager, structureManager }, importer);
  }

  fromDto(
    context: Context,
    dto: ChipModelDto,
    managers?: { logicManager: ITileManagerModel; structureManager: ITileManagerModel },
    importer: RuntimeNodeImport = runtimeGraphIds(context).importer(),
  ): IChipModel {
    // Hydration installs a complete chip. Batch observable writes so topology
    // tracking scans its tiles/instances once, rather than after every push.
    return runInAction(() => this.hydrateChip(context, dto, managers, importer));
  }

  private hydrateChip(
    context: Context,
    dto: ChipModelDto,
    managers?: { logicManager: ITileManagerModel; structureManager: ITileManagerModel },
    importer: RuntimeNodeImport = runtimeGraphIds(context).importer(),
  ): IChipModel {
    const logicCircuitDto = dto.logicCircuitDto as CircuitDto;
    const structureCircuitDto = dto.structureCircuitDto as CircuitDto;
    const editingInstanceDto = dto.editingInstanceDto as unknown as ChipInstanceDto;
    const chipId = dto.id as ChipId;

    const logicCircuitModel = this.circuitModelFactory.fromDto(
      context,
      logicCircuitDto,
      true,
      managers?.logicManager,
      importer,
    );
    let logicCircuit: ILogicCircuitModel | undefined;
    let structureCircuitModel: ICircuitModel | undefined;
    let structureCircuit: IStructureCircuitModel | undefined;
    try {
      logicCircuit = this.logicCircuitModelFactory.create(
        logicCircuitModel,
        context.app.userPreferencesModel.simulationConfig,
        context,
      );
      structureCircuitModel = this.circuitModelFactory.fromDto(
        context,
        structureCircuitDto,
        true,
        managers?.structureManager,
        importer,
      );
      structureCircuit = this.structureCircuitModelFactory.create(structureCircuitModel);

      const editingLogicGraph = LogicGraph.fromDto(
        editingInstanceDto.logicGraphDto,
        runtimeGraphIds(context),
        importer,
      );
      const editingInstance = ChipInstanceModel.fromDto(
        editingInstanceDto,
        context,
        editingLogicGraph,
      );

      if (dto.structureOriginLocationDto) {
        structureCircuit.originLocation = Point.fromData(dto.structureOriginLocationDto);
      }

      const chip = new ChipModel({
        context,
        id: chipId,
        kind: dto.kind,
        templateType: dto.templateType,
        templateOrigin: dto.templateOrigin,
        name: dto.name,
        description: dto.description,
        folder: dto.folder,
        color: dto.color,
        combinedColorDisplay: dto.combinedColorDisplay,
        editingInstance,
        logicCircuit,
        structureCircuit,
      });

      chip.adapterSignals = { ...(dto.adapterSignals ?? {}) };

      // Reconstruct interface links and relink tile references in linear time.
      const logicTiles = new Map(
        logicCircuit.circuitModel.tileManagerModel.tiles.map((t) => [t.id, t]),
      );
      const structureTiles = new Map(
        structureCircuit.circuitModel.tileManagerModel.tiles.map((t) => [t.id, t]),
      );
      for (const linkDto of dto.interfaceLinks) {
        const link = InterfaceLink.fromDto(linkDto);

        if (linkDto.logicTileId) {
          const logicTile = logicTiles.get(
            linkDto.logicTileId as import('@/types/ids').TileId,
          );
          if (logicTile) {
            link.tiles[CircuitType.ChipLogic] = logicTile as any;
          }
        }
        if (linkDto.structureTileId) {
          const structureTile = structureTiles.get(
            linkDto.structureTileId as import('@/types/ids').TileId,
          );
          if (structureTile) {
            link.tiles[CircuitType.ChipStructure] = structureTile as any;
          }
        }

        chip.interfaceLinkMap.set(link.id as InterfaceLinkInstanceId, link);

        if (link.type === InterfaceType.MULTI) {
          link.syncTileIoSides();
        }
      }

      // Reconstruct embedded chip instances
      for (const rawInstanceDto of dto.chipInstanceDtos) {
        const instanceDto = rawInstanceDto as unknown as ChipInstanceDto;
        const instanceGraph = LogicGraph.fromDto(
          instanceDto.logicGraphDto,
          runtimeGraphIds(context),
          new RuntimeNodeImport(runtimeGraphIds(context)),
        );
        const chipInstance = ChipInstanceModel.fromDto(
          instanceDto,
          context,
          instanceGraph,
        );
        logicCircuit.circuitModel.chips.push(chipInstance);
        logicCircuit.chipInstances.set(chipInstance.id, chipInstance);
      }

      // Placed tiles do not serialize effectiveIoSides. Restore these from the
      // child definition just as placement does, before building either graph.
      for (const tile of logicCircuit.circuitModel.tileManagerModel.tiles) {
        if (!tile.meta.partOfChip) continue;
        const instance = logicCircuit.chipInstances.get(
          tile.meta.partOfChip as ChipInstanceId,
        );
        const state = tile.state as InterfaceTileState<unknown> | undefined;
        const linkId = state?.connectedInterface?.interfaceLinkId;
        const link =
          linkId &&
          instance?.chip?.interfaceLinkMap.get(linkId as InterfaceLinkInstanceId);
        if (instance?.chip?.kind === 'bundle-adapter') {
          tile.adapterPin = tile.tileType === TileType.SingleInterface;
          if (tile.tileType === TileType.BundleInterface) {
            tile.adapterPort = true;
            tile.effectiveIoSides = {
              [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
              [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
              [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
              [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
            };
            logicCircuit.circuitModel.connectionModel.breakOptionalConnections(tile.id);
          }
        }
        if (link && link.type === InterfaceType.MULTI) {
          tile.effectiveIoSides = link.computeEffectiveIoSides();
        }
      }

      // IO overrides are hydrated after the saved connection graph. Reconcile
      // them synchronously: MobX reactions run only after the load transaction,
      // while templates and placed instances are built inside that transaction.
      for (const circuit of [logicCircuit.circuitModel, structureCircuit.circuitModel]) {
        const configured = circuit.tileManagerModel.tiles.filter(
          (tile) => tile.effectiveIoSides,
        );
        if (configured.length)
          circuit.connectionModel.recalculateTiles(configured.map((tile) => tile.id));
      }

      return chip;
    } catch (error) {
      logicCircuit?.dispose?.();
      logicCircuitModel.connectionModel.dispose();
      structureCircuitModel?.connectionModel.dispose();
      throw error;
    }
  }
}
